import { useRef } from "react";

import { Modal, Typography } from "neetoui";
import { ActionBlock, Form, Input } from "neetoui/formik";
import PropTypes from "prop-types";
import { type } from "ramda";
import { Trans, useTranslation } from "react-i18next";

import { INITIAL_VALUES } from "./constants";
import { deletionValidationSchema } from "./utils";

const ConfirmationModal = ({
  buttonLabel,
  confirmationText,
  description = "",
  isOpen = true,
  isSubmitting = false,
  title = "",
  onClose,
  onSubmit,
  deleteConfirmationMessage,
}) => {
  const { t } = useTranslation();
  const formikProps = {
    initialValues: INITIAL_VALUES,
    validationSchema: deletionValidationSchema(confirmationText),
    onSubmit,
  };

  const deleteInputLabel = deleteConfirmationMessage || (
    <Trans
      components={{ strong: <strong /> }}
      i18nKey="neetoMolecules.confirmationModal.deleteConfirmationMessage"
      values={{ value: confirmationText }}
    />
  );

  const initialFocusRef = useRef(null);

  return (
    <Modal
      {...{ initialFocusRef, isOpen, onClose }}
      data-testid="confirmation-modal"
    >
      <Modal.Header>
        <Typography data-testid="confirmation-modal-title" style="h2">
          {title}
        </Typography>
      </Modal.Header>
      <Form {...{ formikProps }}>
        {({ values }) => (
          <>
            <Modal.Body className="flex flex-col gap-y-4">
              {type(description) === "String" ? (
                <Typography style="body2">{description}</Typography>
              ) : (
                description
              )}
              <Input
                data-testid="confirmation-modal-input"
                label={deleteInputLabel}
                labelProps={{ className: "block" }}
                name="confirmationText"
                ref={initialFocusRef}
                type="text"
              />
            </Modal.Body>
            <Modal.Footer>
              <ActionBlock
                className="flex items-center gap-x-2"
                cancelButtonProps={{
                  label: t("neetoMolecules.common.actions.cancel"),
                  style: "text",
                  onClick: onClose,
                  disabled: false,
                  type: "button",
                }}
                submitButtonProps={{
                  "data-testid": "delete-continue-button",
                  label: buttonLabel,
                  loading: isSubmitting,
                  style: "danger",
                  disabled:
                    isSubmitting ||
                    confirmationText !== values.confirmationText,
                }}
              />
            </Modal.Footer>
          </>
        )}
      </Form>
    </Modal>
  );
};

ConfirmationModal.propTypes = {
  /**
   * Label of the action button in the Modal
   */
  buttonLabel: PropTypes.string,
  /**
   * Text that is expected to be entered by the user
   */
  confirmationText: PropTypes.string,
  /**
   * Modal description
   */
  description: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * To be passed to loading prop of the action button
   */
  isSubmitting: PropTypes.bool,
  /**
   * To specify whether the modal should be opened or closed.
   */
  isOpen: PropTypes.bool,
  /**
   * Title of the modal
   */
  title: PropTypes.string,
  /**
   * Handler function that is invoked when the close button is clicked
   */
  onClose: PropTypes.func,
  /**
   * Handler function that is invoked when the action button is clicked
   */
  onSubmit: PropTypes.func,
  /**
   * Message that will be shown as the label of the confirmation input field.
   */
  deleteConfirmationMessage: PropTypes.string,
};

export default ConfirmationModal;
